// Refonte du suivi — logique d'écran pure (sans React), partagée par
// Preparer, Modifier et SuiviPage, et testée seule.

import { markClientAppWorkspaceWeekEditorSave } from '../services/clientAppWorkspace';
import { changementsBrouillon, nomVersion } from './services/suiviVersions';

export const MOTIFS_PREPARER = Object.freeze([
  { id: 'rendez_vous', label: 'Après un rendez-vous' },
  { id: 'demande', label: 'À sa demande' },
  { id: 'changement_phase', label: 'Changement de phase' },
]);

export function formaterDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Rendez-vous relus du journal, du plus récent au plus ancien (rang 1-based). */
export function rendezVousRelus(client) {
  const journal = client?.journey_state?.consultations_log;
  if (!Array.isArray(journal)) return [];
  return journal
    .map((entree, index) => ({ entree, ordinal: index + 1 }))
    .filter(({ entree }) => entree?.clinical?.appointment?.reviewed_by_anissa === true)
    .map(({ entree, ordinal }) => {
      const rdv = entree.clinical.appointment;
      return {
        ordinal,
        date: entree.date || null,
        titre: rdv.period_label || `Rendez-vous ${ordinal}`,
        resume: typeof rdv.summary === 'string' ? rdv.summary : '',
        note: typeof rdv.practitioner_note === 'string' ? rdv.practitioner_note : (entree.notes || ''),
        questions: Array.isArray(rdv.questions)
          ? rdv.questions.filter((q) => q?.text).map((q) => ({ texte: q.text, reponse: q.answer || '' }))
          : [],
      };
    })
    .reverse();
}

/**
 * Message de sortie avec des modifications du suivi non enregistrées. Les
 * menus non enregistrés de l'éditeur ne sont PAS gardés dans le navigateur.
 */
export function messageQuitterSuivi({ menus = false, autres = false } = {}) {
  const phrases = [];
  if (menus) phrases.push('Les menus non enregistrés seront perdus.');
  if (autres || !menus) {
    phrases.push(menus
      ? 'Les autres modifications du suivi restent gardées dans ce navigateur et seront reprises à ton retour.'
      : 'Des modifications du suivi ne sont pas encore enregistrées. Elles restent gardées dans ce navigateur et seront reprises à ton retour.');
  }
  return `${phrases.join(' ')} Quitter quand même ?`;
}

/** Nom d'une phase, partout le même : client_name, sinon name, sinon « Phase n ». */
export function nomPhase(phase, position = null) {
  if (!phase) return null;
  return phase.client_name || phase.name || `Phase ${phase.order ?? position ?? '?'}`;
}

/** Libellés des champs du brouillon, pour dire ce qui n'est pas enregistré. */
export const LIBELLES_CHAMP = Object.freeze({
  nutrition_plan: 'Plan',
  supplements: 'Compléments',
  recipes: 'Recettes',
  meal_recipes: 'Semaine, menus & recettes',
  editorial_overrides: 'Semaine, menus & recettes',
  fiche_frigo_json: 'Fiche frigo',
  intro_letter: 'Introduction',
  notes_for_coach: 'Note interne',
  private_notes: 'Notes privées',
  label: 'Nom de la version',
  protocol_phases: 'Phases',
  active_phase_id: 'Phases',
});

/** La phase qui deviendra active (la suivante dans l'ordre du parcours). */
export function phaseSuivante(protocolPhases) {
  const phases = Array.isArray(protocolPhases?.phases) ? protocolPhases.phases : [];
  const index = phases.findIndex((phase) => phase?.status === 'active');
  const suivante = index >= 0 ? phases[index + 1] : null;
  if (!suivante || suivante.status !== 'upcoming') return null;
  return {
    id: String(suivante.id),
    nom: nomPhase(suivante, index + 2),
    depuis: nomPhase(phases[index], index + 1),
  };
}

/** Peut-on lancer la préparation avec ce choix ? */
export function preparationPossible({ motif, rdvOrdinal, rendezVous = [], phase = null }) {
  if (motif === 'rendez_vous') return rendezVous.some((rdv) => rdv.ordinal === rdvOrdinal);
  if (motif === 'demande') return true;
  if (motif === 'changement_phase') return Boolean(phase);
  return false;
}

export const ONGLETS_MODIFIER = Object.freeze([
  { id: 'semaine', label: 'Semaine, menus & recettes' },
  { id: 'plan', label: 'Plan' },
  { id: 'complements', label: 'Compléments' },
]);

export const LIBELLES_MOTIF = Object.freeze({
  rendez_vous: 'Après un rendez-vous',
  demande: 'À sa demande',
  changement_phase: 'Changement de phase',
});

/**
 * Adaptateur du contrat de WeekMenusEditor : onSave(consultation, { kind })
 * doit renvoyer la consultation à jour. On n'envoie que les champs autorisés
 * qui ont changé, par la file d'enregistrement du brouillon.
 */
export function creerAdaptateurSemaine({ lireLigne, enregistrerMaintenant }) {
  return async (prochaine, { kind = 'content' } = {}) => {
    const aEnregistrer = kind === 'content' ? markClientAppWorkspaceWeekEditorSave(prochaine) : prochaine;
    const changes = changementsBrouillon(lireLigne(), aEnregistrer);
    if (Object.keys(changes).length === 0) return lireLigne();
    return enregistrerMaintenant(changes);
  };
}

export const LIBELLES_ETAT = Object.freeze({
  brouillon: 'en préparation',
  validee: 'validée, pas encore publiée',
  publiee: 'publiée',
  remise: 'remise',
  enregistree: 'enregistrée',
});

export const LIBELLES_MOTIF_COURT = Object.freeze({
  initial: 'Programme initial',
  rendez_vous: 'Après un rendez-vous',
  demande: 'À sa demande',
  changement_phase: 'Changement de phase',
});

/** Note interne ajoutée quand la cliente a formulé une demande. */
export function noteDemande(notesActuelles, demande, date = new Date()) {
  const texte = String(demande || '').trim();
  if (!texte) return null;
  const entete = `Demande de la cliente (${formaterDate(date)}) : ${texte}`;
  const avant = typeof notesActuelles === 'string' && notesActuelles.trim() ? `${notesActuelles.trim()}\n\n` : '';
  return `${avant}${entete}`;
}

export function dateSortie(version) {
  return version?.publiee_le || version?.publication_tampon_le || version?.remise_le || null;
}

/**
 * Le bouton « Valider et publier » et, s'il est grisé, la seule chose à faire.
 * Ordre : contrôle, alertes, canaux, aperçu de l'app (obligatoire), PDF.
 */
export function etatBoutonPublier({ controle, canaux = [], apercuVu = false, canalDocument = null, overrideCoche = false, enCours = false }) {
  const non = (raison) => ({ actif: false, raison });
  if (enCours) return non(null);
  if (!controle) return non('Contrôle en cours.');
  if (controle.erreurLivrable) return non('Le contenu de l’app est à corriger.');
  if (controle.narratif?.required && !controle.narratif.confirmed) return non('Coche la relecture des textes.');
  if (controle.bloquant && !(controle.confirmable && overrideCoche)) {
    return non(controle.confirmable ? 'Confirme les alertes ou corrige le contenu.' : 'Corrige le contenu signalé.');
  }
  if (canaux.length === 0) return non('Choisis l’app, le PDF ou les deux.');
  if (canaux.includes('app') && !apercuVu) return non('Ouvre l’aperçu de l’app avant de publier.');
  if (canaux.includes('document') && !canalDocument) return non('Choisis comment tu remets le PDF.');
  return { actif: true, raison: null };
}

const LIBELLES_NOTIFICATION = Object.freeze({
  push_envoye: 'prévenue par notification',
  push_inactif: 'pas de notification active',
});

/**
 * Une ligne de l'historique (vue suivi_versions), la plus récente d'abord :
 * nom, motif, date, état, canaux de sortie, notification. Lecture seule.
 */
export function lignesHistorique(versions) {
  return [...(versions || [])].reverse().map((v) => {
    const canaux = [];
    if (v.publiee_le || v.publication_tampon_le) canaux.push('app');
    if (v.remise_le || v.document_format) canaux.push(v.document_format === 'docx' ? 'Word' : 'PDF');
    const date = dateSortie(v) || v.validee_le || v.created_at || v.date || null;
    return {
      id: v.consultation_id,
      nom: nomVersion(v),
      motif: LIBELLES_MOTIF_COURT[v.motif] || v.motif || '',
      date: formaterDate(date),
      etat: LIBELLES_ETAT[v.etat] || v.etat || '',
      canaux,
      notification: LIBELLES_NOTIFICATION[v.notification_statut] || null,
      ouvrable: v.etat === 'publiee' || v.etat === 'remise' || v.etat === 'validee',
      etatCode: v.etat,
    };
  });
}

/**
 * Mention de l'aperçu d'une version de l'historique : l'aperçu est recalculé
 * depuis la fiche actuelle, il n'est dit identique que si son empreinte
 * égale celle du livrable validé (et publié).
 */
export function mentionApercu({ etat, attendue = null, recalculee = null }) {
  const identique = Boolean(attendue && recalculee && attendue === recalculee);
  if (etat === 'validee') {
    return identique
      ? 'Pas encore sortie · identique à la version validée'
      : 'Pas encore sortie · aperçu recalculé, peut différer de la version validée';
  }
  if (identique && etat === 'publiee') return 'Identique à la version publiée';
  return 'Aperçu recalculé, peut différer de ce que la cliente a reçu';
}

export function resumeDerniereVersion(versions) {
  const sorties = (versions || []).filter((v) => v.etat === 'publiee' || v.etat === 'remise');
  const derniere = sorties[sorties.length - 1];
  if (!derniere) return null;
  const date = formaterDate(dateSortie(derniere));
  return `${nomVersion(derniere)} ${LIBELLES_ETAT[derniere.etat]}${date ? ` le ${date}` : ''}`;
}

